import { useState, useEffect } from 'react';
import { Article, ArticleCategory } from '@/types/data';

interface UseArticlesReturn {
  articles: Article[];
  loading: boolean;
  error: string | null;
  getArticles: (limit?: number) => Promise<Article[]>;
  getArticleCategories: () => Promise<ArticleCategory[]>;
  getArticlesByCategory: (categoryId: number, limit?: number) => Promise<Article[]>;
}

export const useArticles = (): UseArticlesReturn => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getArticles = async (limit?: number): Promise<Article[]> => {
    try {
      setLoading(true);
      setError(null);
      const url = limit ? `/api/articles?limit=${limit}` : '/api/articles';
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        setArticles(data.data);
        return data.data;
      } else {
        setError(data.error || 'Failed to fetch articles');
        return [];
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch articles');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getArticleCategories = async (): Promise<ArticleCategory[]> => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('/api/article-categories');
      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        setError(data.error || 'Failed to fetch article categories');
        return [];
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch article categories');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getArticlesByCategory = async (categoryId: number, limit?: number): Promise<Article[]> => {
    try {
      setLoading(true);
      setError(null);
      const url = limit ? `/api/articles/category/${categoryId}?limit=${limit}` : `/api/articles/category/${categoryId}`;
      const response = await fetch(url);
      const data = await response.json();

      if (data.success) {
        return data.data;
      } else {
        setError(data.error || 'Failed to fetch articles by category');
        return [];
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch articles by category');
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getArticles();
  }, []);

  return { articles, loading, error, getArticles, getArticleCategories, getArticlesByCategory };
};